import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { Attachment, attachmentFilename, fileStem, Organization, User } from '@ee/common/models';
import { Subscription } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnnotationMode, NgxExtendedPdfViewerService, pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { DocumentService, UploadClient } from '@ee/common/services';
import { DOCX_FILE_MIME_TYPES, PDF_FILE_EXTENSION } from '@ee/common/constants';
import { canFillPdfForms } from './can-fill-pdf-forms';

/**
 * Self-hosted pdf.js runtime assets, copied out of `ngx-extended-pdf-viewer` by the `angular.json`
 * asset globs. Left to itself the viewer fetches its worker, viewer bundle, cMaps, standard fonts and
 * wasm decoders from a third-party CDN at runtime, putting that CDN in the critical path of every
 * document preview.
 *
 * The globs are deliberately narrow rather than `assets/**`: the shipped bundle is 21 MB across 455
 * files, including 114 locale folders and both ES5 and unminified duplicates, and `ngsw-config.json`
 * puts `/assets/**` in a prefetch-on-update asset group — so a wholesale copy would add hundreds of
 * hashed entries to `ngsw.json` for nothing.
 */
const PDFJS_ASSETS_FOLDER = 'assets/pdfjs';

/** Resolved against `baseURI` so a non-root base href still works. */
const PDFJS_ASSETS_URL = new URL(`${PDFJS_ASSETS_FOLDER}/`, document.baseURI).toString();

@Component({
  selector: 'ee-pdf-preview-dialog',
  template: `
    @if (!hideTitle) {
      <h2 mat-dialog-title>{{ title ?? 'Preview' }}</h2>
    }
    <div mat-dialog-content class="pdf-wrapper">
      @if (!pdfSrc) {
        <div class="flex flex-row flex-1 justify-center items-center">
          <ee-loading message="Loading..."></ee-loading>
        </div>
      } @else {
        <!-- sidebarVisible=false: the viewer opens its page-thumbnail rail by default, which eats a
             third of a dialog that exists to show one document. There is no toolbar here to reopen it
             from, so it stays closed. -->
        <ngx-extended-pdf-viewer class="viewer" [src]="pdfSrc" [showToolbar]="false"
                                 [sidebarVisible]="false"
                                 [showBorders]="true" [textLayer]="true" [zoom]="'page-width'"
                                 [imageResourcesPath]="imageResourcesPath"
                                 [localeFolderPath]="localeFolderPath" [language]="'en-US'"
                                 [showEditorButtons]="false" [showTextEditor]="false"
                                 [showStampEditor]="false" [showDrawEditor]="false"
                                 [showHighlightEditor]="false" [showSignatureEditor]="false"
                                 [showCommentEditor]="false"
                                 (pdfLoaded)="onPdfLoaded()"></ngx-extended-pdf-viewer>
      }
    </div>
    @if (saveError) {
      <div class="save-error" role="alert">{{ saveError }}</div>
    }
    <div mat-dialog-actions class="ee-modal-actions">
      <button mat-button [mat-dialog-close]="null" type="button" [disabled]="saving"
        data-tracking-event="pdf-preview-dialog-close">CLOSE</button>
      <!-- SAVE sits before DOWNLOAD, so a fillable document reads CLOSE / SAVE / DOWNLOAD. With no
           SAVE the @if renders nothing and the row is CLOSE / DOWNLOAD exactly as before, which is
           why this needs no condition of its own. -->
      @if (showSave) {
        <button mat-flat-button color="primary" (click)="saveFilledForm()" type="button" [disabled]="saving"
          data-tracking-event="pdf-preview-dialog-save">{{ saving ? 'SAVING…' : 'SAVE' }}</button>
      }
      <button mat-flat-button color="accent" (click)="saveDocumentAsPdf()" type="button" [disabled]="saving"
        data-tracking-event="pdf-preview-dialog-download">DOWNLOAD</button>
    </div>
  `,
  styles: [`
    ee-pdf-preview-dialog {
      .pdf-wrapper {
        flex: 1;
        min-height: 70vh;
        max-height: 100%;
        // hidden, not auto: the viewer scrolls inside #viewerContainer, so auto here produced a
        // SECOND scrollbar around the first — one to move the page, one to move the box holding it.
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .viewer {
          flex: 1;
          min-height: 70vh;
          height: 100%;
          width: 100%;
          display: block;

          // Scrolling is the viewer's own job: pdf.js scrolls #viewerContainer, and #outerContainer
          // is the layout shell around it. Forcing overflow on BOTH — which this used to do, with
          // !important so it beat the library — produced two nested scrollbars, one to move the page
          // and one to move the box holding it.
          //
          // The override existed to defeat an app-wide '.ng2-pdf-viewer-container { overflow-x:
          // hidden !important }' rule, and that rule is gone: it named the OLD library's wrapper,
          // and no '.ngx-extended-pdf-viewer-container' exists in this package for it to have been
          // renamed onto. So there is nothing left to override.
          //
          // #outerContainer is pinned to hidden rather than simply left alone, so it cannot become a
          // second scroller again.
          #outerContainer {
            overflow: hidden;
            width: 100%;
            height: 100%;
          }

          // pdf.js sizes .page as content-box: it writes the content width/height inline and adds
          // a 9px transparent border for the page shadow. Tailwind preflight's global
          // 'box-sizing: border-box' makes that border eat 18px out of the box, so the canvas
          // (100%/100% of .page) renders ~1.4% smaller than the JS-sized .annotationLayer and
          // .textLayer siblings. The offset is zero at the top-left of a page and grows toward the
          // bottom-right. Restore what pdf.js assumes.
          .pdfViewer .page {
            box-sizing: content-box;
          }
        }
      }

      .save-error {
        padding: 0 24px 8px;
        color: var(--ee-error, #b3261e);
        font-size: 0.875rem;
      }
    }
  `],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class PdfPreviewDialogComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];
  docxFileMimeType = DOCX_FILE_MIME_TYPES;
  title = 'Preview';
  attachment: Attachment;
  hideTitle = false;
  pdfSrc: string | Uint8Array;
  imageResourcesPath = `${PDFJS_ASSETS_URL}images/`;
  localeFolderPath = `${PDFJS_ASSETS_FOLDER}/locale`;

  /**
   * Whether SAVE is offered: the dialog is in fillable mode (gate passed, opened against a case) AND
   * the document turned out to have form fields. Resolved after load, because an AcroForm is only
   * detectable once the document is parsed — and set true if that detection fails, since the fields
   * are editable regardless and offering nothing would leave them persisting nothing.
   */
  showSave = false;

  saving = false;
  saveError: string | null = null;

  /**
   * Whether the fields render as editable inputs. Decided BEFORE load, from the gate alone — see the
   * constructor.
   */
  private readonly fillable: boolean;

  constructor(
    private documentService: DocumentService,
    private uploadClient: UploadClient,
    private pdfViewerService: NgxExtendedPdfViewerService,
    public cdr: ChangeDetectorRef,
    private dialogRef: MatDialogRef<PdfPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      attachment: Attachment,
      downloadUrl: string | Uint8Array,
      hideTitle: boolean,
      /** Present only when the dialog is opened against a case whose attachment can be written. */
      caseId?: string,
      org?: Organization,
      user?: User,
      /** Active e-filing subscription. Editing is a paid capability; without it, preview only. */
      efilingSubscribed?: boolean
    }
  ) {
    this.attachment = data.attachment;
    this.pdfSrc = data.downloadUrl;
    this.hideTitle = !!data.hideTitle;
    this.fillable = !!data.caseId && canFillPdfForms(data.org, data.user, data.attachment, data.efilingSubscribed);

    // Set in the constructor — before the template instantiates <ngx-extended-pdf-viewer> — because
    // the viewer copies pdfDefaultOptions into PDFViewerApplicationOptions once at init and never
    // re-reads it. That is also why the mode cannot depend on whether the document has an AcroForm:
    // that is only knowable after the document loads, which is too late.
    //
    // Deciding from the gate alone is nonetheless correct, because a flat PDF renders identically
    // under either mode — it has no widget annotations for ENABLE_FORMS to draw.
    //
    // AnnotationMode.ENABLE makes pdf.js paint each field's own appearance stream into the page
    // canvas, so a read-only preview matches the downloaded file exactly. ENABLE_FORMS instead draws
    // the fields as HTML inputs over the canvas, which is what makes them editable — and which, in a
    // read-only preview, would drift out of alignment with the raster, re-typeset the field text in
    // the browser rather than using the PDF's own, and offer inputs that persist nothing.
    //
    // NOTE for future readers: the viewer's `[disableForms]` input is NOT the way to get the
    // read-only rendering. It walks the already-rendered annotation layer and sets `disabled` on the
    // inputs, so the misaligned browser-typeset overlay is still there, just greyed out.
    pdfDefaultOptions.assetsFolder = PDFJS_ASSETS_FOLDER;
    pdfDefaultOptions.annotationMode = this.fillable ? AnnotationMode.ENABLE_FORMS : AnnotationMode.ENABLE;
  }

  ngOnInit(): void {
    // The display name (EE-349/EE-350), so the preview's title agrees with the list it was opened
    // from — a renamed document must not resurface its uploaded name here.
    this.title = attachmentFilename(this.attachment) || this.attachment.description || this.title;
  }

  /**
   * The name this document is known by, with a `.pdf` extension.
   *
   * `attachmentFilename` is the one display name (EE-349): `generated_filename` when set, else the
   * uploaded name. Falling back to `generateFilenameFromPath` only when there is neither keeps a
   * legacy upload downloadable — but then the dialog title and the saved file can disagree, which is
   * exactly what the naming work removes.
   */
  private displayPdfFilename(): string {
    const stem = fileStem(attachmentFilename(this.attachment));
    return stem
      ? `${stem}.${PDF_FILE_EXTENSION}`
      : this.documentService.generateFilenameFromPath(this.attachment, PDF_FILE_EXTENSION);
  }

  /**
   * Decides whether SAVE is offered, once the document is parsed.
   *
   * `getFormData()` returns the document's form fields; an empty list means the PDF is flat, and a
   * flat PDF has nothing to fill no matter who is looking at it.
   */
  async onPdfLoaded(): Promise<void> {
    // Nothing to ask on behalf of someone who cannot save either way. Without the gate the preview
    // is exactly what it was before this feature existed — no SAVE, and nothing said about it.
    if (!this.fillable) {
      return;
    }
    try {
      const fields = await this.pdfViewerService.getFormData();
      this.showSave = Array.isArray(fields) && fields.length > 0;
    } catch {
      // Offer SAVE anyway. We are already in ENABLE_FORMS mode — decided before load and unchangeable
      // now — so the fields on screen ARE editable. Hiding SAVE here would produce exactly the state
      // the constructor comment argues against: inputs that accept typing and persist nothing. A
      // pointless save on a flat document is a far smaller harm than silently discarded work, and the
      // server rejects anything it should.
      this.showSave = true;
    }
    this.cdr.markForCheck();
  }

  /**
   * Downloads what the user is looking at.
   *
   * In fillable mode that means the viewer's current state, not [pdfSrc] — otherwise someone who fills
   * the form and reaches for DOWNLOAD instead of SAVE silently gets the blank original back. Falls
   * back to the stored bytes if the viewer cannot produce a blob.
   */
  async saveDocumentAsPdf(): Promise<void> {
    const filename = this.displayPdfFilename();

    if (this.fillable) {
      try {
        const edited = await this.pdfViewerService.getCurrentDocumentAsBlob();
        if (edited) {
          this.documentService.saveDocument(filename, edited);
          return;
        }
      } catch {
        // Fall through to the stored bytes rather than failing the download outright.
      }
    }

    if (typeof this.pdfSrc === 'string') {
      this.documentService.saveDocumentFromUrl(filename, this.pdfSrc);
    } else {
      const blob = new Blob([this.pdfSrc.buffer as ArrayBuffer], { type: 'application/pdf' });
      this.documentService.saveDocument(filename, blob);
    }
  }

  /**
   * Saves the filled form as a new version of this attachment.
   *
   * blob → staged upload → version endpoint. The upload goes straight to S3 via a presigned PUT and is
   * scanned there like any other upload, so the edited bytes never pass through the app server and a
   * malicious PDF is caught by the same gate.
   *
   * On failure the dialog stays open with the user's edits intact — closing it would throw away typed
   * work over a transient error.
   */
  async saveFilledForm(): Promise<void> {
    if (this.saving || !this.data.caseId || !this.attachment.id) {
      return;
    }
    this.saving = true;
    this.saveError = null;
    // The scan poll runs for up to 90s. Escape or a backdrop click during it would destroy the
    // dialog, and ngOnDestroy unsubscribing the parked upload triggers its teardown, which deletes
    // the staged object — the user's typing gone with no error and nothing saved. Block dismissal
    // for the duration instead; CLOSE is disabled in the template for the same reason.
    this.dialogRef.disableClose = true;
    this.cdr.markForCheck();

    try {
      const blob = await this.pdfViewerService.getCurrentDocumentAsBlob();
      if (!blob) {
        throw new Error('Could not read the filled form back from the viewer.');
      }
      // The display name, so the staged object and the version it becomes are named the way the
      // document is known — and so the server's extension-derived format check sees `.pdf`.
      const file = new File([blob], this.displayPdfFilename(), { type: 'application/pdf' });

      const { upload_id } = await this.firstValue(this.uploadClient.uploadAndFinalize(file));
      const updated = await this.firstValue(
        this.documentService.saveAttachmentVersion(this.data.caseId, this.attachment.id, upload_id)
      );
      this.dialogRef.disableClose = false;
      this.dialogRef.close(updated);
    } catch (e: unknown) {
      this.saveError = this.messageFor(e);
    } finally {
      // In a `finally` so no failure path can strand the dialog with every button disabled and no
      // message — including one that never throws but never resolves either.
      if (this.saveError) {
        this.saving = false;
        this.dialogRef.disableClose = false;
        this.cdr.markForCheck();
      }
    }
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  /**
   * `firstValueFrom` over a subscription tracked in [subs], so a save in flight when the dialog is
   * destroyed does not leak its HTTP subscription.
   */
  private firstValue<T>(source: import('rxjs').Observable<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      let emitted = false;
      const sub = source.subscribe({
        next: value => {
          emitted = true;
          resolve(value);
        },
        error: reject,
        // Without this a source that completes without emitting never settles the promise, and the
        // save latches on with every button disabled and no message. `uploadAndFinalize` completes
        // right after its single emission, so this only fires on the empty case.
        complete: () => {
          if (!emitted) {
            reject(new Error('The save did not complete. Please try again.'));
          }
        }
      });
      this.subs.push(sub);
    });
  }

  private messageFor(e: unknown): string {
    if (e instanceof Error && e.message) {
      return e.message;
    }
    return 'Could not save the filled form. Please try again.';
  }
}
